import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { PhotoGrid } from "@/components/gallery/PhotoGrid";
import { useGallery } from "@/hooks/useGallery";
import { scrollToId } from "@/lib/scroll";

const EmptyState = () => (
    <div data-testid="gallery-empty" className="mt-14 border border-white/10 p-14 text-center">
        <Camera size={32} className="mx-auto text-tl-orange" strokeWidth={1.5} />
        <p className="mt-5 font-display text-lg font-semibold text-white">Galleria in aggiornamento</p>
        <p className="mt-2 text-sm text-tl-mutedLight">Le foto dei nostri lavori saranno disponibili a breve.</p>
    </div>
);

export default function Galleria() {
    const { photos } = useGallery();

    return (
        <main data-testid="galleria-page" className="bg-tl-dark2 pb-24 pt-32 md:pt-36">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <Link
                        to="/"
                        data-testid="galleria-back-link"
                        className="inline-flex items-center gap-2 font-mono-tl text-xs uppercase tracking-[0.2em] text-tl-mutedLight transition-colors hover:text-tl-orange"
                    >
                        <ArrowLeft size={14} /> Torna alla home
                    </Link>
                </Reveal>
                <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                    <div>
                        <Reveal delay={0.05}>
                            <Eyebrow number="04" label="Galleria" />
                        </Reveal>
                        <Reveal delay={0.12}>
                            <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                                I nostri lavori<span className="text-tl-orange">.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
                                Pezzi, stampi, prototipi e momenti di officina: uno sguardo diretto
                                su come lavoriamo il ferro e l'alluminio a Brivio.
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <Link
                            to="/#contatti"
                            data-testid="galleria-cta"
                            onClick={() => setTimeout(() => scrollToId("contatti"), 150)}
                            className="group inline-flex items-center gap-3 bg-tl-orange px-7 py-3.5 font-mono-tl text-xs font-semibold uppercase tracking-[0.2em] text-tl-dark transition-colors duration-300 hover:bg-tl-orangeHover"
                        >
                            Richiedi un preventivo
                            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>

                {photos === null && (
                    <div data-testid="gallery-loading" className="mt-14 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="h-60 animate-pulse bg-tl-card" />
                        ))}
                    </div>
                )}
                {photos && photos.length === 0 && <EmptyState />}
                {photos && photos.length > 0 && (
                    <div className="mt-14">
                        <p data-testid="gallery-count" className="mb-5 font-mono-tl text-[11px] uppercase tracking-[0.25em] text-white/40">
                            {photos.length} {photos.length === 1 ? "foto" : "foto"} — clicca per ingrandire
                        </p>
                        <PhotoGrid photos={photos} />
                    </div>
                )}
            </div>
        </main>
    );
}
